<script setup>
import { computed } from 'vue'
import { Logo, Typography } from '@aryeo/runway'
import Badge from '@/Runway/Elements/Badge.vue'

import { useCurrentRoute } from '@/Hooks/Shared/useCurrentRoute.js'

const emits = defineEmits(['itemClicked'])

const props = defineProps({
  name: String,
  image: String,
  iconComponent: Function,
  tag: String,
  targetRouteName: String,
  targetRouteAttributes: Object,
  isInertiaLink: Boolean,
  routeNamesToCheckIfCurrent: Array,
  itemOfPrimaryColumn: Boolean,
  ghost: {
    type: Boolean,
    default: false,
  },
  ordersRequiringActionCount: {
    type: Number,
    default: 0,
  },
  totalPendingMediaRequestCount: {
    type: Number,
    default: 0,
  },
  badge: String,
})

const { isCurrentForRouteNames } = useCurrentRoute(props.routeNamesToCheckIfCurrent)

const linkComponent = computed(() => (props.isInertiaLink ? 'aryeo-link' : 'a'))
const targetUrl = computed(() =>
  props.targetRouteName ? route(props.targetRouteName, props.targetRouteAttributes) : '#'
)

const handleItemClick = (e) => {
  emits('itemClicked', e)
}
</script>

<template>
  <component
    :is="linkComponent"
    :href="targetUrl"
    :ghost="ghost"
    prefetch
    @click="handleItemClick"
    :class="[
      isCurrentForRouteNames ? 'bg-theme-100 font-bold' : 'hover:bg-theme-50 font-medium',
      'w-full group flex items-center p-3 text-sm rounded-xl focus:outline-none focus:bg-theme-50 focus-visible:shadow-focus',
    ]"
  >
    <Logo v-if="image" :image="image" size="xsmall" class="mr-3"></Logo>
    <component
      v-else
      :is="iconComponent"
      class="text-black mr-3 shrink-0 h-5 w-5"
      aria-hidden="true"
    />

    <Typography
      as="span"
      :variant="isCurrentForRouteNames ? 'body-bold' : 'body'"
      class="leading-tightest"
    >
      {{ name }}
    </Typography>
    <div
      v-if="name === 'Orders' && ordersRequiringActionCount > 0"
      class="ml-auto inline-flex items-center px-2.5 py-0.5 -my-0.5 rounded-full text-xs font-bold bg-tag-warning -mr-2"
    >
      {{ ordersRequiringActionCount }}
    </div>
    <div
      v-if="name === 'Media Requests' && totalPendingMediaRequestCount > 0"
      class="ml-auto inline-flex items-center px-2.5 py-0.5 -my-0.5 rounded-full text-xs font-bold bg-tag-warning -mr-2"
    >
      {{ totalPendingMediaRequestCount }}
    </div>
    <Badge v-if="badge" :text="badge" textVariant="label-small" class="-mr-2" />
    <span
      v-if="tag"
      :class="[
        isCurrentForRouteNames ? 'bg-white' : 'bg-gray-100 group-hover:bg-gray-200',
        'ml-3 inline-block py-0.5 px-3 text-xs font-bold rounded-full',
      ]"
    >
      {{ tag }}
    </span>
  </component>
</template>
